import React, { useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Bold, Italic, List, ListOrdered, Link2, Undo2, Redo2 } from 'lucide-react';

interface Props {
  value: string;
  onChange: (html: string) => void;
  testId?: string;
}

const ToolbarBtn = ({ active, onClick, title, children, testId }: {
  active?: boolean; onClick: () => void; title: string; children: React.ReactNode; testId: string;
}) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${active ? 'bg-[hsl(210,40%,93%)] text-[hsl(214,58%,41%)]' : 'hover:bg-secondary/60 text-muted-foreground'}`}
    data-testid={testId}
  >
    {children}
  </button>
);

/** Editor rich-text minimale (grassetto, corsivo, liste, link) — l'HTML viene
 * comunque sanificato lato server con whitelist di tag. */
export function RichTextEditor({ value, onChange, testId = 'richtext' }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [3, 4] } }),
      Link.configure({ openOnClick: false, autolink: false, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL del link', previous || 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="rounded-lg border bg-white overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b bg-[hsl(var(--dtk-canvas))]">
        <ToolbarBtn testId={`${testId}-bold`} title="Grassetto" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-3.5 h-3.5" /></ToolbarBtn>
        <ToolbarBtn testId={`${testId}-italic`} title="Corsivo" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-3.5 h-3.5" /></ToolbarBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn testId={`${testId}-ul`} title="Elenco puntato" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-3.5 h-3.5" /></ToolbarBtn>
        <ToolbarBtn testId={`${testId}-ol`} title="Elenco numerato" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-3.5 h-3.5" /></ToolbarBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn testId={`${testId}-link`} title="Link" active={editor.isActive('link')} onClick={setLink}><Link2 className="w-3.5 h-3.5" /></ToolbarBtn>
        <div className="flex-1" />
        <ToolbarBtn testId={`${testId}-undo`} title="Annulla" onClick={() => editor.chain().focus().undo().run()}><Undo2 className="w-3.5 h-3.5" /></ToolbarBtn>
        <ToolbarBtn testId={`${testId}-redo`} title="Ripeti" onClick={() => editor.chain().focus().redo().run()}><Redo2 className="w-3.5 h-3.5" /></ToolbarBtn>
      </div>
      <EditorContent
        editor={editor}
        className="job-richtext px-3 py-2.5 text-sm min-h-[180px] max-h-[420px] overflow-y-auto focus:outline-none"
      />
    </div>
  );
}
